<template>
  <div
    v-if="desc"
    class="player-info-txt"
    @click="clickPlayerInfo('desc')"
  >
    <span
      ref="mention"
      class="desc"
      v-html="formatDesc"
    />
    <strong
      v-if="topic.id"
      class="topic"
      @click.stop="clickPlayerInfo('topic')"
    > #{{ topic.name || topic.id }}</strong>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { ModelMetaFeed } from '@/types/model';
import { IHashMap } from '@/types/data';
import { encodeHtml } from '@/utils/string';
import { IS_SSR } from '@/utils/env';

const enum EEmitEvent {
  onClickInfo = 'on-click-info',
}

export default defineComponent({
  props: {
    feed: {
      type: Object as PropType<ModelMetaFeed>,
      default: () => ({}),
    },
  },
  emits: [EEmitEvent.onClickInfo],
  data() {
    return {
      mentionUid: [] as string[],
      formatDesc: '',
      onDestroyCbs: [] as (() => void) [],
    };
  },
  computed: {
    desc() {
      const { feedDesc, videoConfig } = this.feed;
      return videoConfig?.title || feedDesc;
    },
    topic() {
      const { videoConfig, topic, topicId } = this.feed;
      return {
        id: videoConfig?.tid || videoConfig?.tid || topicId,
        name: videoConfig?.topic?.topic_name || topic?.name,
      };
    },
  },
  watch: {
    feed() {
      // SSR阶段不可处理事件
      if (!IS_SSR) {
        this.offsetHandler();
      }
      this.mentionUid = [];
      this.init();
    },
  },
  created() {
    this.init();
  },
  mounted() {
    this.findMention();
  },
  beforeUnmount() {
    this.mentionUid = [];
    this.offsetHandler();
  },
  methods: {
    getFormatDesc() {
      const desc = this.getFeedDesc(this.desc);
      let index = -1;
      this.formatDesc = encodeHtml(desc)
        .replace(/@(.*?)\s/g, (str, $1) => ` <strong class="ws-mention" data-index="${index += 1}">@${$1}</strong> `);
    },
    clickPlayerInfo(tag: string, params?: IHashMap) {
      this.$emit(EEmitEvent.onClickInfo, tag, params);
    },
    getFeedDesc(input: string) {
      const reg = /(?:@\{uid:(\w+)\s*,\s*nick:(.*?)(?:\s*,\s*who:(\d))?(?:\s*,\s*auto:(\d))?\})|(?:\[em\]e(\d{1,7})\[\/em\])|(?:#([^#]+)?#)|($)/gim;
      return input.replace(reg, (curr, uid, originNick) => {
        let nick;
        if (uid) {
          try {
            nick = decodeURIComponent(originNick);
          } catch (err) {
            nick = '(null)'; // 和客户端一致
          }
          this.mentionUid.push(uid);
          return `@${nick} `;
        }
        return curr;
      });
    },
    findMention() {
      const mention = this.$refs.mention as HTMLElement;
      const doMention = (e: Event) => {
        const el = e.target as HTMLElement;
        // 事件委托
        if (el.nodeName === 'STRONG') {
          e.stopPropagation();
          const index = el.getAttribute('data-index');
          if (index !== null) {
            this.clickPlayerInfo('person', { uid: this.mentionUid[Number(index)] });
          }
        }
      };
      if (!mention) return;
      mention.addEventListener('click', doMention);
      this.onDestroyCbs.push(() => mention.removeEventListener('click', doMention));
    },
    // 移除绑定的事件
    offsetHandler() {
      this.onDestroyCbs.forEach((cb) => {
        if (typeof cb === 'function') {
          cb();
        }
      });
      this.onDestroyCbs = [];
    },
    init() {
      this.getFormatDesc();
    },
  },
});
</script>

<style lang="scss" scoped>
.player-info-txt {
  display: -webkit-box;
  overflow: hidden;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  @include responsiveStyleSingle(font-size, 3.7333, 'vw');
  @include responsiveStyleSingle(line-height, 5.8667, 'vw');
  .desc {
    margin-right: 5px;
  }
  > strong {
    display: inline-block;
    font-weight: bold;
  }
  .topic {
    display: inline-block;
  }
}
</style>
